/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { VideoMetaDTO } from '@west-australian-newspapers/publication-types'
import { IconChevronUp } from '../icons/IconChevronUp/IconChevronUp'

import {
    StyledOverlay,
    StyledPlayerWrapper,
    StyledAdPlaceholder,
    StyledArrowUp,
    StyledArrowDown,
    StyledTopBar,
    StyledCloseButton,
    StyledFeedScroll,
    StyledScreen,
    StyledConsentContainer,
    StyledVideoInfoOverlay,
    StyledVideoInfoTitle,
    StyledVideoInfoDescription,
} from './VerticalVideoHubControls.styled'

import { ArticleVideo } from '../publication/ArticleVideo/ArticleVideo'
import { SevenVideoDescription } from '../VideoHub/VideoSkinV2/VideoDescription'
import {
    AdEvent,
    CardEvents,
    CardItem,
    CollectionDataLoaded,
    ContentDataTypes,
    DataLoaderGlobalParams,
    ConsentLevel,
    useConsentGiven,
    DataLayerEventName,
} from '@news-mono/web-common'
import { MaybeLoaded } from '@west-australian-newspapers/json-react-layouts-data-loader'
import { VerticalVideoAdSlot } from './VerticalVideoAdSlot/VerticalVideoAdSlot'
import { useVerticalAdFlow } from './VerticalVideoAdSlot/utils/useVerticalAdFlow'
import { mapTopVideoMeta } from '../VideoHub/helpers/mapToVideoMeta'
import { useLoadMore } from '../data-controllers/LoadMore/useLoadMore'
import { useGdprMode } from '../content/Video/utils/gdpr-mode'
import { ConsentFallback } from '../tracking/ConsentFallback/ConsentFallback'

interface Props {
    videos: VideoMetaDTO[]
    startVideoIndex: number
    onClose: () => void

    onEvent: (event: CardEvents | AdEvent) => void
    adUnitPath: string
    showAds?: boolean
    currentPath: string
    publicUrl: string
    loadMoreDataDefinition?: ContentDataTypes
    loadMoreInitialData?: MaybeLoaded<CollectionDataLoaded>
    loadMoreServices?: DataLoaderGlobalParams
}

export const VerticalVideoHubControls: React.FC<Props> = ({
    videos,
    startVideoIndex,
    onClose,
    onEvent,
    adUnitPath,
    showAds = true,
    currentPath,
    publicUrl,
    loadMoreDataDefinition,
    loadMoreInitialData,
    loadMoreServices,
}) => {
    const feedScrollRef = useRef<HTMLDivElement | null>(null)
    const screenRefs = useRef<(HTMLDivElement | null)[]>([])
    const activePlayerRef = useRef<HTMLDivElement | null>(null)
    const overlayRef = useRef<HTMLDivElement | null>(null)
    const autoScrollRef = useRef(false)
    const autoScrollTimeoutRef = useRef<number | null>(null)
    const wheelLockUntilRef = useRef(0)
    const wheelAccumulatedDeltaRef = useRef(0)
    const wheelGestureLockedRef = useRef(false)
    const wheelGestureUnlockTimeoutRef = useRef<number | null>(null)
    const lastLoadMoreAtCountRef = useRef<number | null>(null)
    const lastLoadMoreAtTimeRef = useRef<number>(0)
    const initialFeedLengthRef = useRef<number | null>(null)
    const loadMoreDefinitionRef = useRef(loadMoreDataDefinition)
    const loadMoreInitialDataRef = useRef(loadMoreInitialData)
    const loadMoreServicesRef = useRef(loadMoreServices)
    const gdprMode = useGdprMode()
    const consentGiven = useConsentGiven({
        consentRequiredLevel: ConsentLevel.Advertising | ConsentLevel.Analytics,
    })
    const canLoadVideoPlayer = gdprMode || consentGiven

    const shouldUseLoadMore =
        !!loadMoreDefinitionRef.current &&
        !!loadMoreInitialDataRef.current &&
        !!loadMoreServicesRef.current
    const {
        loadMore,
        isLoading: isLoadingMore,
        data: loadMoreData,
    } = useLoadMore(
        shouldUseLoadMore
            ? (loadMoreDefinitionRef.current as ContentDataTypes)
            : ({} as ContentDataTypes),
        shouldUseLoadMore
            ? (loadMoreInitialDataRef.current as MaybeLoaded<CollectionDataLoaded>)
            : ({ loaded: false } as MaybeLoaded<CollectionDataLoaded>),
        shouldUseLoadMore
            ? (loadMoreServicesRef.current as DataLoaderGlobalParams)
            : ({} as DataLoaderGlobalParams),
    )

    const isListingLike =
        loadMoreData.loaded &&
        (loadMoreData.result.kind === 'listing' ||
            loadMoreData.result.kind === 'video-listing')
    const listingResult = isListingLike
        ? (loadMoreData.result as {
              publications: CardItem[]
              loadMorePossible: boolean
          })
        : null
    const effectiveVideos = listingResult
        ? mapTopVideoMeta(listingResult.publications)
        : videos
    const loadMorePossible = listingResult
        ? listingResult.loadMorePossible
        : false

    useEffect(() => {
        if (!shouldUseLoadMore) return
    }, [shouldUseLoadMore, loadMoreData, loadMorePossible, isLoadingMore])

    const adsEvery = 5
    const shouldShowAds = showAds && !!adUnitPath
    const adVideo = effectiveVideos[effectiveVideos.length - 1]
    const feedItems = useMemo(() => {
        const items: Array<
            { type: 'video'; videoIndex: number } | { type: 'ad' }
        > = []

        effectiveVideos.forEach((_, videoIndex) => {
            items.push({ type: 'video', videoIndex })
            if (shouldShowAds && (videoIndex + 1) % adsEvery === 0) {
                items.push({ type: 'ad' })
            }
        })

        return items
    }, [effectiveVideos, shouldShowAds])

    const startFeedIndex = useMemo(() => {
        const foundIndex = feedItems.findIndex(
            (item) =>
                item.type === 'video' && item.videoIndex === startVideoIndex,
        )
        return foundIndex === -1 ? 0 : foundIndex
    }, [feedItems, startVideoIndex])

    const [index, setIndex] = useState(startFeedIndex)
    const [loadedMap, setLoadedMap] = useState<Record<number, boolean>>({})
    const [arrowOffset, setArrowOffset] = useState(0)
    const [isShowMore, setIsShowMore] = useState(true)
    const [playWhenReadyIndex, setPlayWhenReadyIndex] = useState<number | null>(
        null,
    )

    useEffect(() => {
        if (initialFeedLengthRef.current === null && feedItems.length > 0) {
            initialFeedLengthRef.current = feedItems.length
        }
    }, [feedItems.length])

    useEffect(() => {
        if (!shouldUseLoadMore) return
        if (!loadMorePossible || isLoadingMore) return
        const initialFeedLength =
            initialFeedLengthRef.current ?? feedItems.length
        const triggerIndex = Math.max(0, Math.floor(initialFeedLength * 0.8))
        if (index < triggerIndex) return
        if (lastLoadMoreAtCountRef.current === feedItems.length) return
        const now = Date.now()
        if (now - lastLoadMoreAtTimeRef.current < 3000) return
        lastLoadMoreAtTimeRef.current = now
        lastLoadMoreAtCountRef.current = feedItems.length
        loadMore(loadMoreData as any)
    }, [
        feedItems.length,
        index,
        isLoadingMore,
        loadMorePossible,
        loadMore,
        loadMoreData,
        shouldUseLoadMore,
    ])

    useEffect(() => {
        const updateViewportHeight = () => {
            const height = window.visualViewport?.height || window.innerHeight
            document.documentElement.style.setProperty(
                '--vertical-vh',
                `${height * 0.01}px`,
            )
        }

        updateViewportHeight()
        window.addEventListener('resize', updateViewportHeight)
        window.visualViewport?.addEventListener('resize', updateViewportHeight)

        return () => {
            window.removeEventListener('resize', updateViewportHeight)
            window.visualViewport?.removeEventListener(
                'resize',
                updateViewportHeight,
            )
        }
    }, [])

    const initialMute = (() => {
        try {
            const saved = localStorage.getItem('verticalHubMuted')
            return saved === null ? true : saved === 'true'
        } catch {
            return true
        }
    })()

    const [isGlobalMuted, setIsGlobalMuted] = useState(initialMute)

    const saveMute = (mute: boolean) => {
        try {
            localStorage.setItem('verticalHubMuted', mute ? 'true' : 'false')
        } catch {}
    }

    useEffect(() => {
        saveMute(initialMute)
    }, [initialMute])

    const handleMuteChange = (muted: boolean) => {
        setIsGlobalMuted(muted)
        saveMute(muted)
    }

    useEffect(() => {
        setIndex(startFeedIndex)
    }, [startFeedIndex])

    const activeItem = feedItems[index]
    const activeVideoIndex =
        activeItem?.type === 'video' ? activeItem.videoIndex : null
    const currentVideo =
        activeVideoIndex !== null ? effectiveVideos[activeVideoIndex] : null
    const headline = currentVideo?.name || currentVideo?.description || ''
    const rawDescription =
        currentVideo?.longDescription ||
        currentVideo?.description ||
        currentVideo?.name ||
        ''
    const description = rawDescription !== headline ? rawDescription : ''

    // Pause all videos except currently active
    const pauseAllVideosExcept = (activeIdx: number) => {
        screenRefs.current.forEach((screen, i) => {
            if (!screen || i === activeIdx) return
            const video = screen.querySelector(
                'video',
            ) as HTMLVideoElement | null
            if (video) {
                video.pause()
                if (video.readyState > 0) {
                    video.currentTime = 0
                }
            }
        })
    }

    // When opening vertical hub, pause any background videos outside the hub
    useEffect(() => {
        const overlay = overlayRef.current
        if (!overlay) return
        const videos = Array.from(document.querySelectorAll('video'))
        videos.forEach((video) => {
            if (overlay.contains(video)) return
            video.pause()
            if (video.readyState > 0) {
                video.currentTime = 0
            }
        })
    }, [])

    // scroll to initial snap
    useEffect(() => {
        screenRefs.current[startFeedIndex]?.scrollIntoView({ behavior: 'auto' })
    }, [startFeedIndex])

    // Move focus into the feed so keyboard scrolling targets it without a click first
    useEffect(() => {
        feedScrollRef.current?.focus({ preventScroll: true })
    }, [])

    // detect which screen is active based on viewport position
    const handleScroll = () => {
        if (autoScrollRef.current) {
            return
        }
        const winH = window.innerHeight
        const viewportCenter = winH / 2
        let newIdx = -1
        let bestDistance = Number.POSITIVE_INFINITY

        screenRefs.current.forEach((el, i) => {
            if (!el) return
            const rect = el.getBoundingClientRect()
            const isVisible = rect.bottom > 0 && rect.top < winH
            if (!isVisible) return

            const center = rect.top + rect.height / 2
            const distance = Math.abs(center - viewportCenter)

            if (distance < bestDistance) {
                bestDistance = distance
                newIdx = i
            }
        })

        if (newIdx !== -1 && newIdx !== index) {
            if (
                isAdScreenActive &&
                adSkippableMap[index] &&
                feedItems[index]?.type === 'ad'
            ) {
                markAdHidden(index)
            }
            setIndex(newIdx)
        }
    }

    const scrollToIndex = (targetIndex: number) => {
        if (targetIndex < 0 || targetIndex >= feedItems.length) return
        if (
            isAdScreenActive &&
            adSkippableMap[index] &&
            feedItems[index]?.type === 'ad' &&
            targetIndex !== index
        ) {
            markAdHidden(index)
        }
        autoScrollRef.current = true
        if (autoScrollTimeoutRef.current) {
            window.clearTimeout(autoScrollTimeoutRef.current)
        }
        screenRefs.current[targetIndex]?.scrollIntoView({
            behavior: 'smooth',
        })
        setIndex(targetIndex)
        setTimeout(() => {
            pauseAllVideosExcept(targetIndex)
        }, 250)
        autoScrollTimeoutRef.current = window.setTimeout(() => {
            autoScrollRef.current = false
        }, 500)
    }

    const [adSkippableMap, setAdSkippableMap] = useState<
        Record<number, boolean>
    >({})
    const {
        adHiddenMap,
        suppressAutoplayOnce,
        setSuppressAutoplayOnce,
        isAdScreenActive,
        shouldLockNavigation,
        findNextVisibleIndex,
        markAdHidden,
        hideAdAndNavigate,
    } = useVerticalAdFlow({
        feedItems,
        index,
        scrollToIndex,
        adSkippableMap,
    })

    const prevVisibleIndex = findNextVisibleIndex(index - 1, -1)
    const nextVisibleIndex = findNextVisibleIndex(index + 1, 1)
    const canPrevVisible = prevVisibleIndex !== null
    const canNextVisible = nextVisibleIndex !== null

    // scroll to previous
    const handlePrev = () => {
        if (!canPrevVisible || prevVisibleIndex === null) return
        scrollToIndex(prevVisibleIndex)
    }

    // scroll to next
    const handleNext = () => {
        if (!canNextVisible || nextVisibleIndex === null) return
        scrollToIndex(nextVisibleIndex)
    }

    useEffect(() => {
        const onEscPress = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose()
            }
        }

        window.addEventListener('keydown', onEscPress)
        return () => {
            window.removeEventListener('keydown', onEscPress)
        }
    }, [])

    useEffect(() => {
        const feedScroll = feedScrollRef.current
        if (!feedScroll) return

        const WHEEL_STEP_THRESHOLD = 36
        const WHEEL_LOCK_MS = 1000
        const WHEEL_GESTURE_IDLE_MS = 1500

        const scheduleWheelGestureUnlock = () => {
            if (wheelGestureUnlockTimeoutRef.current) {
                window.clearTimeout(wheelGestureUnlockTimeoutRef.current)
            }

            wheelGestureUnlockTimeoutRef.current = window.setTimeout(() => {
                wheelGestureLockedRef.current = false
                wheelAccumulatedDeltaRef.current = 0
            }, WHEEL_GESTURE_IDLE_MS)
        }

        const onWheel = (event: WheelEvent) => {
            if (!feedItems.length) return
            if (event.deltaY === 0) return

            event.preventDefault()
            scheduleWheelGestureUnlock()

            if (wheelGestureLockedRef.current) {
                return
            }

            if (autoScrollRef.current) {
                return
            }

            const now = Date.now()
            if (now < wheelLockUntilRef.current) {
                return
            }

            wheelAccumulatedDeltaRef.current += event.deltaY
            if (
                Math.abs(wheelAccumulatedDeltaRef.current) <
                WHEEL_STEP_THRESHOLD
            ) {
                return
            }

            const direction = wheelAccumulatedDeltaRef.current > 0 ? 1 : -1
            wheelAccumulatedDeltaRef.current = 0
            wheelLockUntilRef.current = now + WHEEL_LOCK_MS
            wheelGestureLockedRef.current = true

            if (direction > 0) {
                if (nextVisibleIndex !== null) {
                    scrollToIndex(nextVisibleIndex)
                }
                return
            }

            if (prevVisibleIndex !== null) {
                scrollToIndex(prevVisibleIndex)
            }
        }

        feedScroll.addEventListener('wheel', onWheel, { passive: false })
        return () => {
            feedScroll.removeEventListener('wheel', onWheel)
            if (wheelGestureUnlockTimeoutRef.current) {
                window.clearTimeout(wheelGestureUnlockTimeoutRef.current)
                wheelGestureUnlockTimeoutRef.current = null
            }
            wheelGestureLockedRef.current = false
        }
    }, [feedItems.length, nextVisibleIndex, prevVisibleIndex, scrollToIndex])

    useEffect(() => {
        wheelAccumulatedDeltaRef.current = 0
    }, [index])

    // calculate arrow offset
    useEffect(() => {
        const wrapper = activePlayerRef.current
        if (!wrapper) return

        const resize = () => {
            const playerWidth = wrapper.offsetWidth
            const viewportWidth = window.innerWidth
            const sideSpace = (viewportWidth - playerWidth) / 2
            const extra = 42

            setArrowOffset(sideSpace - extra)
        }

        resize()
        window.addEventListener('resize', resize)
        return () => window.removeEventListener('resize', resize)
    }, [index])

    // disable page scroll
    useEffect(() => {
        const prevBody = document.body.style.overflow
        const prevHtml = document.documentElement.style.overflow

        document.body.style.overflow = 'hidden'
        document.documentElement.style.overflow = 'hidden'

        return () => {
            document.body.style.overflow = prevBody
            document.documentElement.style.overflow = prevHtml
            if (autoScrollTimeoutRef.current) {
                window.clearTimeout(autoScrollTimeoutRef.current)
                autoScrollTimeoutRef.current = null
            }
        }
    }, [])

    // autoplay active video when index changes (including initial index)
    useEffect(() => {
        pauseAllVideosExcept(index)
        setIsShowMore(true)
    }, [index])

    useEffect(() => {
        setPlayWhenReadyIndex(null)
        const timeout = window.setTimeout(() => {
            if (suppressAutoplayOnce) {
                setSuppressAutoplayOnce(false)
                return
            }
            if (activeVideoIndex !== null) {
                setPlayWhenReadyIndex(activeVideoIndex)
            }
        }, 0)

        return () => {
            window.clearTimeout(timeout)
        }
    }, [activeVideoIndex, suppressAutoplayOnce])

    // Guard against late-playing videos on non-active screens
    useEffect(() => {
        const listeners: Array<{
            video: HTMLVideoElement
            onPlay: () => void
            onLoaded: () => void
        }> = []

        screenRefs.current.forEach((screen, i) => {
            const video = screen?.querySelector(
                'video',
            ) as HTMLVideoElement | null
            if (!video) return

            const onPlay = () => {
                if (i !== index) {
                    video.pause()
                }
            }

            const onLoaded = () => {
                if (i !== index) {
                    video.pause()
                }
            }

            video.addEventListener('play', onPlay)
            video.addEventListener('loadedmetadata', onLoaded)
            listeners.push({ video, onPlay, onLoaded })
        })

        return () => {
            listeners.forEach(({ video, onPlay, onLoaded }) => {
                video.removeEventListener('play', onPlay)
                video.removeEventListener('loadedmetadata', onLoaded)
            })
        }
    }, [index, loadedMap])

    const markLoaded = (idx: number) => {
        setLoadedMap((prev) => (prev[idx] ? prev : { ...prev, [idx]: true }))
    }

    /**
     * video.available fires once on mount, which can happen while a screen is
     * still a preloaded neighbor (not yet active). Cache the latest one per
     * screen and replay it the first time that screen becomes active, in case
     * it arrived while preloading (e.g. behind an ad screen).
     */
    const lastAvailableEventRef = useRef<Record<number, CardEvents>>({})
    /**
     * Screens are never unmounted once loaded, so the underlying player keeps
     * its started/milestone state forever. Track which videos have already been
     * active so a revisit can force a remount instead of relying on a stale player
     * instance that won't refire video.start/milestones.
     */
    const previouslyActiveIndexesRef = useRef<Set<number>>(new Set())
    const [restartTokens, setRestartTokens] = useState<Record<number, number>>(
        {},
    )

    useEffect(() => {
        if (activeVideoIndex === null) return

        if (previouslyActiveIndexesRef.current.has(activeVideoIndex)) {
            setRestartTokens((prev) => ({
                ...prev,
                [activeVideoIndex]: (prev[activeVideoIndex] ?? 0) + 1,
            }))
            return
        }

        previouslyActiveIndexesRef.current.add(activeVideoIndex)
        const cached = lastAvailableEventRef.current[activeVideoIndex]
        if (cached) onEvent(cached)
    }, [activeVideoIndex])

    // intercept internal ArticleVideo events
    const handleVideoEvent = (eventIdx: number) => (event: CardEvents) => {
        const type = (event as any)?.type?.toLowerCase?.() || ''
        const isAvailable = type === DataLayerEventName.videoAvailable

        if (isAvailable) {
            lastAvailableEventRef.current[eventIdx] = event
        }

        if (activeItem?.type === 'ad') {
            markLoaded(eventIdx)
            return
        }

        // Only forward events from the active (currently playing) screen
        if (eventIdx === activeVideoIndex) {
            onEvent(event)
        }
        markLoaded(eventIdx)

        const isEnd =
            type === 'video-ended' ||
            type === 'video_end' ||
            type === 'video.ended' ||
            (type.includes('video') && type.includes('end'))

        if (
            eventIdx === activeVideoIndex &&
            isEnd &&
            index < feedItems.length - 1
        ) {
            handleNext()
        }
    }

    const autoplayOptions = {
        delayDuration: 0,
        pauseWhenOutOfView: false,
        pauseCountdownOutsideViewport: false,
        scrollElement: feedScrollRef.current,
    }

    const preloadAutoplayOptions = {
        delayDuration: 0,
        pauseWhenOutOfView: true,
        pauseCountdownOutsideViewport: false,
        scrollElement: feedScrollRef.current,
    }

    const getArrowStyle = (
        offset: number,
    ): React.CSSProperties & Record<string, string> => ({
        pointerEvents: 'auto',
        '--arrow-offset': `calc(100vw - ${offset}px)`,
    })

    if (!canLoadVideoPlayer) {
        return (
            <StyledOverlay ref={overlayRef}>
                <StyledTopBar style={{ pointerEvents: 'auto' }}>
                    <StyledCloseButton onClick={onClose}>✕</StyledCloseButton>
                </StyledTopBar>
                <StyledFeedScroll style={{ overflow: 'hidden' }}>
                    <StyledScreen>
                        <StyledConsentContainer>
                            <ConsentFallback />
                        </StyledConsentContainer>
                    </StyledScreen>
                </StyledFeedScroll>
            </StyledOverlay>
        )
    }

    return (
        <StyledOverlay ref={overlayRef}>
            <StyledTopBar style={{ pointerEvents: 'auto' }}>
                <StyledCloseButton onClick={onClose}>✕</StyledCloseButton>
            </StyledTopBar>

            <StyledFeedScroll
                ref={feedScrollRef}
                tabIndex={-1}
                onScroll={handleScroll}
                style={{
                    overflowY: shouldLockNavigation ? 'hidden' : 'scroll',
                    outline: 'none',
                }}
            >
                {feedItems.map((item, feedIndex) => {
                    const isActive = feedIndex === index
                    const isNeighbor = Math.abs(feedIndex - index) === 1

                    if (item.type === 'ad') {
                        if (!adVideo) {
                            return null
                        }
                        if (adHiddenMap[feedIndex]) {
                            // Keep the slot mounted (empty) so its removal
                            // doesn't shrink scroll height and shift the
                            // viewport onto the following screen. It's taken
                            // out of scroll-snap so swiping can't rest on it.
                            return (
                                <StyledScreen
                                    key={`ad-${feedIndex}`}
                                    ref={(el) =>
                                        (screenRefs.current[feedIndex] = el)
                                    }
                                    style={{
                                        scrollSnapAlign: 'none',
                                        pointerEvents: 'none',
                                    }}
                                />
                            )
                        }
                        return (
                            <StyledScreen
                                key={`ad-${feedIndex}`}
                                ref={(el) =>
                                    (screenRefs.current[feedIndex] = el)
                                }
                            >
                                <StyledPlayerWrapper
                                    ref={
                                        feedIndex === index
                                            ? activePlayerRef
                                            : null
                                    }
                                    style={{ pointerEvents: 'auto' }}
                                >
                                    <StyledAdPlaceholder>
                                        <VerticalVideoAdSlot
                                            adUnitPath={adUnitPath}
                                            isMuted={isGlobalMuted}
                                            onMuteToggle={() =>
                                                handleMuteChange(!isGlobalMuted)
                                            }
                                            onSkippableChange={(isSkippable) =>
                                                setAdSkippableMap((prev) => {
                                                    const nextValue =
                                                        isSkippable || false
                                                    if (
                                                        prev[feedIndex] ===
                                                        nextValue
                                                    ) {
                                                        return prev
                                                    }
                                                    return {
                                                        ...prev,
                                                        [feedIndex]: nextValue,
                                                    }
                                                })
                                            }
                                            onEvent={onEvent}
                                            onAdComplete={() => {
                                                hideAdAndNavigate(feedIndex)
                                            }}
                                            onAdError={() => {
                                                hideAdAndNavigate(feedIndex)
                                            }}
                                            isActive={feedIndex === index}
                                            videoId={adVideo.id}
                                            publicationPathname={currentPath}
                                            position={feedIndex}
                                            pagePosition="vertical-video-carousel"
                                        />
                                    </StyledAdPlaceholder>
                                </StyledPlayerWrapper>
                            </StyledScreen>
                        )
                    }

                    const video = effectiveVideos[item.videoIndex]
                    const shouldRender =
                        isActive || isNeighbor || loadedMap[item.videoIndex]

                    return (
                        <StyledScreen
                            key={video.id}
                            ref={(el) => (screenRefs.current[feedIndex] = el)}
                        >
                            <StyledPlayerWrapper
                                ref={
                                    feedIndex === index ? activePlayerRef : null
                                }
                                style={{ pointerEvents: 'auto' }}
                            >
                                {shouldRender && (
                                    <ArticleVideo
                                        key={`${video.id}-${
                                            restartTokens[item.videoIndex] ?? 0
                                        }`}
                                        isVerticalVideo={true}
                                        videoDetails={{
                                            id: video.id,
                                            accountId: video.accountId,
                                            heading: video.name || '',
                                            videoType: video.videoType as
                                                | 'vod'
                                                | 'live'
                                                | null,
                                            isVerticalVideo: true,
                                        }}
                                        showTitles={false}
                                        showPlaylist={false}
                                        autoplayOptions={
                                            isActive
                                                ? autoplayOptions
                                                : isNeighbor
                                                ? preloadAutoplayOptions
                                                : false
                                        }
                                        autoplayNextOptions={false}
                                        pauseOverlay={true}
                                        pagePosition="vertical-video-carousel"
                                        publicationPathname={currentPath}
                                        publicUrl={publicUrl}
                                        playWhenReady={
                                            playWhenReadyIndex ===
                                            item.videoIndex
                                        }
                                        videoOptions={{
                                            // Ads are temporarily disabled for vertical overlay videos.
                                            adUnitPath: '',
                                            canAutoplayVideos: true,
                                            enableSnowplow: false,
                                            enablePrebid: false,
                                            isMagniteEnabled: false,
                                            gdprMode: false,
                                            hidePoster: true,
                                        }}
                                        onEvent={handleVideoEvent(
                                            item.videoIndex,
                                        )}
                                        forcedMuted={isGlobalMuted}
                                        onMuteChange={handleMuteChange}
                                        getVideoQueue={() =>
                                            Promise.resolve({
                                                items: [
                                                    {
                                                        ...video,
                                                        topicMeta:
                                                            video.topicMeta ??
                                                            undefined,
                                                    },
                                                ],
                                                type: 'not-playlist-video',
                                            })
                                        }
                                    />
                                )}
                                {isActive &&
                                    activeVideoIndex !== null &&
                                    (headline || description) && (
                                        <StyledVideoInfoOverlay>
                                            {headline && (
                                                <StyledVideoInfoTitle>
                                                    {headline}
                                                </StyledVideoInfoTitle>
                                            )}
                                            {description && (
                                                <StyledVideoInfoDescription>
                                                    <SevenVideoDescription
                                                        description={
                                                            description
                                                        }
                                                        showMore={isShowMore}
                                                        showMoreHandler={() =>
                                                            setIsShowMore(
                                                                (prev) => !prev,
                                                            )
                                                        }
                                                    />
                                                </StyledVideoInfoDescription>
                                            )}
                                        </StyledVideoInfoOverlay>
                                    )}
                            </StyledPlayerWrapper>
                        </StyledScreen>
                    )
                })}
            </StyledFeedScroll>

            {(canPrevVisible || isAdScreenActive) && (
                <StyledArrowUp
                    onClick={handlePrev}
                    style={getArrowStyle(arrowOffset)}
                    disabled={shouldLockNavigation || !canPrevVisible}
                >
                    <IconChevronUp
                        width={24}
                        height={24}
                        stroke="currentColor"
                    />
                </StyledArrowUp>
            )}

            {(canNextVisible || isAdScreenActive) && (
                <StyledArrowDown
                    onClick={handleNext}
                    style={getArrowStyle(arrowOffset)}
                    disabled={shouldLockNavigation || !canNextVisible}
                >
                    <IconChevronUp
                        width={24}
                        height={24}
                        stroke="currentColor"
                    />
                </StyledArrowDown>
            )}
        </StyledOverlay>
    )
}
